uniform float uTime;
uniform float uDeltaTime;
uniform vec3 uMouse;
uniform float uMouseRadius;
uniform float uMouseAspect;
uniform float uSuckAmount;
uniform float uSuckStrength;
uniform vec3 uMouseVelocity;
uniform float uWindStrength;
uniform float uDamping;
uniform float uTurbulenceStrength;
uniform float uTurbulenceFrequency;
uniform float uFlowFieldFrequency;
uniform float uFlowFieldStrength;
uniform float uFlowFieldInfluence;
uniform sampler2D uBase;
uniform sampler2D uBase2;
uniform float uMorphAmount;
uniform float uMorphStrength;
uniform float uSuctionBarsEnabled;
uniform float uSuctionBarStrength;
uniform float uSuctionBarRadius;
uniform vec3 uSuctionBar0Pos;
uniform vec3 uSuctionBar0Size;
uniform vec3 uSuctionBar1Pos;
uniform vec3 uSuctionBar1Size;
uniform float uUpdraftStrength;
uniform float uCameraZ;
uniform float uFlatten;
uniform float uPathEnabled;
uniform vec3 uPathStart;
uniform vec3 uPathEnd;
uniform float uPathAttraction;
uniform float uPathDriftSpeed;
uniform float uPathDriftRate;
uniform float uPathWaveFreq;
uniform float uPathWaveAmp;
uniform float uPathWaveSpeed;
uniform float uPathSpiralFreq;
uniform float uPathRadius;
uniform float uPathSpread;
uniform float uTextAvoidRadius;
uniform float uTextTipScale;
uniform float uTextTipDown;
uniform float uTextTipAttract;
uniform float uTextTipReach;
uniform float uTextSolid;
uniform vec4 uTextBox0; // xy = centro, zw = half-size (piano z=0)
uniform float uTextBox0Strength;
uniform vec4 uTextBox1;
uniform float uTextBox1Strength;
uniform vec2 uTextTips0; // x = punta sopra, y = punta sotto (unita' mondo, clampate per non toccarsi)
uniform vec2 uTextTips1;
uniform float uTextDodge; // 1 = schivata cinematica (rimappatura x), 0 = forze
uniform float uDomainHalfW;
uniform float uForceGradEnabled;    // 1 = modula flow+turbolenza con un gradiente spaziale
uniform float uForceGradFloor;      // moltiplicatore della forza in alto-centro (0..1)
uniform float uForceGradVertical;   // peso dell'asse verticale alto->basso (0..1)
uniform float uForceGradHorizontal; // peso dell'asse centro->bordi (0..1)
uniform float uForceGradPower;      // curva: >1 allarga la zona calma e concentra l'agitazione al fondo/bordi
uniform float uSymmetricField;      // 1 = flow+turbolenza speculari rispetto all'asse x (scena simmetrica)

#include ../includes/simplexNoise4d.glsl
#include ../includes/textShapeSdf.glsl

vec3 suctionBarForce(vec3 pos, vec3 center, vec3 halfSize, float radius, float strength)
{
    vec3 d = pos - center;
    vec3 clamped = clamp(d, -halfSize, halfSize);
    vec3 closest = center + clamped;
    vec3 toBar = closest - pos;
    float dist = length(toBar);
    if(dist < 0.0001 || dist > radius) return vec3(0.0);
    float proximity = smoothstep(radius, 0.0, dist);
    return (toBar / dist) * proximity * strength;
}

// Flusso laminare attorno al testo: invece di respingere radialmente, la velocita'
// viene deflessa tangenzialmente lungo il contorno della sagoma (pillola con punte
// sopra/sotto), cosi' le streamline si aprono sulla punta inferiore, abbracciano i
// fianchi e si richiudono sulla punta superiore.
// Il box (piano z=0) e' scalato alla profondita' della particella (screen-space corretto).
vec3 textFlowAround(vec3 pos, vec3 vel, vec4 box, vec2 tips, float radius, float strength)
{
    // Prisma infinito lungo z: stessa sezione a ogni profondita', nessun
    // aggiramento possibile davanti o dietro al testo
    vec2 h = box.zw;
    vec2 p = pos.xy - box.xy;
    float tip = tips.x;
    float tipDown = tips.y;
    float g = clamp(strength * 0.1, 0.0, 1.0); // rampa con la visibilita' del testo
    // Attrazione "a camino" oltre l'uscita del flusso (flusso discendente:
    // sotto la cupola inferiore): un attrattore ad ampio raggio fa curvare le
    // particelle verso il centro-basso, richiamandole da tutta la zona sotto
    // la scritta
    if(uTextTipAttract > 0.001)
    {
        vec2 toTip = vec2(0.0, -(h.y + tip + uTextTipReach * 0.25)) - p;
        float td = length(toTip);
        if(td < uTextTipReach && td > 0.0001)
        {
            float tw = (1.0 - td / uTextTipReach) * g;
            // Solo sotto la scritta: chi sta sopra non viene risucchiato contro la sagoma
            tw *= smoothstep(0.0, h.y + tip * 0.5, -p.y);
            vel.xy += (toTip / td) * tw * tw * strength * uTextTipAttract * uDeltaTime;
        }
    }
    float sd = textShapeSdf(p, h, tip, tipDown);
    if(sd > radius) return vel;
    vec2 n = textShapeNormal(p, h, tip, tipDown);
    float w = (1.0 - clamp(sd / radius, 0.0, 1.0)) * g;

    if(uTextSolid > 0.5)
    {
        // Modalita' ostacolo solido: deflessione tangente + scivolamento + cuscinetto
        float vn = dot(vel.xy, n);
        vel.xy -= n * min(vn, 0.0) * w * w;
        // Scivolamento sempre verso il fianco piu' vicino e verso il basso
        // (flusso discendente): chi arriva contro il corpo piatto viene
        // smaltito ai lati, niente ristagni
        vec2 tang = vec2(-n.y, n.x);
        vec2 pref = normalize(vec2((p.x >= 0.0 ? 1.0 : -1.0) * 0.7, -1.0));
        if(dot(tang, pref) < 0.0) tang = -tang;
        vel.xy += tang * w * w * strength * 0.3 * uDeltaTime;
        vel.xy += n * w * w * strength * 0.1 * uDeltaTime;
        if(sd < 0.0) vel.xy += n * (-sd) * 10.0 * g * uDeltaTime * strength * 0.2;
    }
    else
    {
        // Modalita' campo di forza: spinta pura lungo la normale, stessa sagoma.
        // Cresce avvicinandosi al contorno e diventa molto decisa all'interno.
        float inside = sd < 0.0 ? clamp(-sd / max(radius * 0.5, 0.001), 0.0, 1.0) : 0.0;
        vel.xy += n * (w * w + inside * (2.0 + inside * 4.0)) * strength * uDeltaTime;
        // Scivolamento tangenziale come in modalita' solida: la sola spinta
        // normale contiene le particelle ma non le evacua, e sui fianchi si
        // formano ristagni. La tangente verso il fianco piu' vicino e verso il
        // basso (flusso discendente) drena la zona lungo il contorno.
        vec2 tang = vec2(-n.y, n.x);
        vec2 pref = normalize(vec2((p.x >= 0.0 ? 1.0 : -1.0) * 0.7, -1.0));
        if(dot(tang, pref) < 0.0) tang = -tang;
        vel.xy += tang * w * w * strength * 0.3 * uDeltaTime;
    }
    return vel;
}

// Attenuazione della turbolenza vicino alla sagoma del testo: la turbolenza
// ributta le particelle contro il contorno, la repulsione le riespelle e il
// damping azzera il moto netto — ristagno sui fianchi. Vicino alla sagoma il
// flusso torna laminare (0 sul contorno, pieno oltre il raggio di avoid).
float textTurbulenceDamp(vec3 pos, vec4 box, vec2 tips, float radius, float strength)
{
    if(strength < 0.01) return 1.0;
    float sd = textShapeSdf(pos.xy - box.xy, box.zw, tips.x, tips.y);
    float damp = smoothstep(0.0, radius, sd);
    // Segue la rampa di visibilita' della sagoma (stessa di textFlowAround)
    return mix(1.0, damp, clamp(strength * 0.1, 0.0, 1.0));
}

// Rectangular distance: returns 0..1 normalized distance inside the box
float rectDist(vec3 pos, vec3 center, float radius, float aspect)
{
    vec3 d = abs(pos - center);
    float dx = d.x / (radius * aspect);
    float dy = d.y / (radius / aspect);
    float dz = d.z / radius;
    return max(max(dx, dy), dz);
}

void main()
{
    float time = uTime * 0.2;
    vec2 uv = gl_FragCoord.xy / resolution.xy;
    vec4 velocity = texture(uVelocity, uv);
    vec4 particle = texture(uParticles, uv);

    // Reset velocity when particle is dead
    if(particle.a >= 1.0)
    {
        gl_FragColor = vec4(0.0);
        return;
    }

    vec4 base = texture(uBase, uv);

    // Gradiente spaziale della forza (pagina carbon): le forze di agitazione
    // (flow + turbolenza) crescono dall'alto verso il basso e dal centro verso i
    // bordi. Il moto resta calmo e compatto in alto-centro e diventa
    // progressivamente piu' disperso in basso e ai lati — e' proprio questa
    // variazione spaziale (non un valore costante) a far leggere il flusso come
    // pulviscolo nell'aria invece che come lamine d'acqua.
    float forceGrad = 1.0;
    if(uForceGradEnabled > 0.5)
    {
        float yg = clamp((5.8 - particle.y) / 11.6, 0.0, 1.0);                    // 0 in alto, 1 in basso
        float xg = clamp(abs(particle.x) / max(uDomainHalfW, 0.001), 0.0, 1.0);   // 0 al centro, 1 ai bordi
        // unione morbida dei due assi, ciascuno pesabile in modo indipendente
        float g = 1.0 - (1.0 - yg * uForceGradVertical) * (1.0 - xg * uForceGradHorizontal);
        g = pow(g, uForceGradPower); // accentua il contrasto: zona calma piu' ampia in alto-centro
        forceGrad = mix(uForceGradFloor, 1.0, g);
    }

    // Campo simmetrico: campiona il rumore su |x| e specchia la componente x
    // della forza, cosi' le due meta' dello schermo sono immagini speculari
    // (il centro e' vuoto per la repulsione, quindi nessuna cucitura visibile)
    vec3 fieldPos = uSymmetricField > 0.5 ? vec3(abs(particle.x), particle.y, particle.z) : particle.xyz;
    float fieldSgn = (uSymmetricField > 0.5 && particle.x < 0.0) ? -1.0 : 1.0;

    // Flow field force (2 noise calls — combined axes from single noise derivatives)
    if(uFlowFieldStrength > 0.001)
    {
        float influence = (uFlowFieldInfluence - 0.5) * (-2.0);
        float n1 = simplexNoise4d(vec4(fieldPos * uFlowFieldFrequency, time));
        float n2 = simplexNoise4d(vec4(fieldPos * uFlowFieldFrequency + 7.0, time));
        vec3 flowField = normalize(vec3(n1, n2, n1 * n2));
        flowField.x *= fieldSgn;
        float strength = smoothstep(influence, 1.0, n1);
        velocity.xyz += flowField * strength * uFlowFieldStrength * forceGrad * uDeltaTime;
    }

    // Mouse wind force — localized around cursor
    vec3 mouseVel = uMouseVelocity;
    float mouseSpeedSq = dot(mouseVel, mouseVel);
    if(uSuckAmount < 0.1 && mouseSpeedSq > 0.01)
    {
        float windRadius = uMouseRadius * 0.3;
        vec3 diff = particle.xyz - uMouse;
        float distSq = dot(diff, diff);
        float windRadiusSq = windRadius * windRadius;
        if(distSq < windRadiusSq)
        {
            float dist = sqrt(distSq);
            float proximity = smoothstep(windRadius, 0.0, dist);
            velocity.xyz += mouseVel * proximity * uWindStrength * uDeltaTime;
        }
    }

    // Suck toward mouse (when clicking) — spherical area
    if(uSuckAmount > 0.01)
    {
        vec3 toMouse = uMouse - particle.xyz;
        float dist = length(toMouse);
        if(dist < uMouseRadius && dist > 0.0001)
        {
            vec3 suckDir = toMouse / dist;
            float proximity = smoothstep(uMouseRadius, 0.0, dist);
            velocity.xyz += suckDir * proximity * uSuckAmount * uSuckStrength * uDeltaTime;
        }
    }

    // Morph attraction — pull particles toward current morph target
    if(uMorphStrength > 0.0)
    {
        vec3 base1 = texture(uBase, uv).xyz;
        vec3 base2 = texture(uBase2, uv).xyz;
        vec3 morphTarget = mix(base1, base2, uMorphAmount);
        vec3 toTarget = morphTarget - particle.xyz;
        velocity.xyz += toTarget * uMorphStrength * uDeltaTime;
    }

    // Turbulence (2 noise calls — derive third axis)
    if(uTurbulenceStrength > 0.001)
    {
        float t1 = simplexNoise4d(vec4(fieldPos * uTurbulenceFrequency, time * 3.0 + 10.0));
        float t2 = simplexNoise4d(vec4(fieldPos * uTurbulenceFrequency, time * 3.0 + 20.0));
        vec3 turbulence = vec3(t1, t2, t1 - t2);
        turbulence.x *= fieldSgn;
        float turbDamp = min(
            textTurbulenceDamp(particle.xyz, uTextBox0, uTextTips0, uTextAvoidRadius, uTextBox0Strength),
            textTurbulenceDamp(particle.xyz, uTextBox1, uTextTips1, uTextAvoidRadius, uTextBox1Strength));
        velocity.xyz += turbulence * uTurbulenceStrength * turbDamp * forceGrad * uDeltaTime;
    }

    // Fixed suction bars (lateral attractors)
    if(uSuctionBarsEnabled > 0.5)
    {
        velocity.xyz += suctionBarForce(particle.xyz, uSuctionBar0Pos, uSuctionBar0Size, uSuctionBarRadius, uSuctionBarStrength) * uDeltaTime;
        velocity.xyz += suctionBarForce(particle.xyz, uSuctionBar1Pos, uSuctionBar1Size, uSuctionBarRadius, uSuctionBarStrength) * uDeltaTime;
    }

    // Corrente verticale costante (pagina carbon; negativa = discendente)
    if(abs(uUpdraftStrength) > 0.001)
        velocity.y += uUpdraftStrength * uDeltaTime;

    // Percorso a corsie (stile "oryzo" adattato): ogni particella risale lungo una
    // propria corsia verticale distribuita su tutta la larghezza dello schermo,
    // serpeggiando con curve armoniche pseudo-casuali (doppia sinusoide con fase,
    // frequenza e ampiezza per-particella). Il gap attorno ai testi lo scava la
    // sagoma repulsiva, che devia le corsie centrali a destra e sinistra.
    if(uPathEnabled > 0.5)
    {
        // Parametri per-particella (hash stabile dall'uv)
        float h1 = fract(sin(dot(uv, vec2(12.9898, 78.233))) * 43758.5453);
        float h2 = fract(sin(dot(uv, vec2(39.3468, 11.135))) * 24634.6345);
        float h3 = fract(sin(dot(uv, vec2(73.156, 52.235))) * 9284.1234);
        float laneX = (h1 * 2.0 - 1.0) * uPathSpread;
        float ph = h2 * 6.2831853;
        float ff = 0.6 + h3 * 1.2;

        float y = particle.y;
        float wave = uPathWaveAmp * sin(y * uPathWaveFreq * ff + ph + uTime * uPathWaveSpeed)
                   + uPathRadius * sin(y * uPathWaveFreq * ff * 2.7 + ph * 1.7 - uTime * uPathWaveSpeed * 0.6);
        float targetX = laneX + wave;
        float targetZ = (h2 * 2.0 - 1.0) * 1.5;

        float blend = 1.0 - exp(-uDeltaTime * uPathAttraction);
        velocity.x -= (particle.x - targetX) * blend;
        velocity.z -= (particle.z - targetZ) * blend;

        // Risalita a velocita' di crociera
        velocity.y += (uPathDriftSpeed - velocity.y) * (1.0 - exp(-uDeltaTime * uPathDriftRate));
    }

    // Flusso attorno ai testi (pagina carbon).
    // In modalita' schivata (uTextDodge) qui non serve nulla: la deviazione e'
    // una rimappatura di posizione nel position shader, incrementale e fluida
    // perche' il gap varia con continuita' lungo la risalita — la velocita'
    // verticale non viene mai toccata (zero stalli, zero catapulte laterali)
    if(uTextDodge < 0.5)
    {
        if(uTextBox0Strength > 0.01)
            velocity.xyz = textFlowAround(particle.xyz, velocity.xyz, uTextBox0, uTextTips0, uTextAvoidRadius, uTextBox0Strength);
        if(uTextBox1Strength > 0.01)
            velocity.xyz = textFlowAround(particle.xyz, velocity.xyz, uTextBox1, uTextTips1, uTextAvoidRadius, uTextBox1Strength);
    }

    // Damping
    velocity.xyz *= (1.0 - uDamping * uDeltaTime);

    // Modalita' 2D: tutto ragiona sul piano z=0
    if(uFlatten > 0.5) velocity.z = 0.0;

    gl_FragColor = velocity;
}
